/* eslint-disable react/prop-types */

import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Col, Form } from 'react-bootstrap';
import { FormLabel } from './FormLabel';
import ErrorFeedback from './ErrorFeedback';

/**
 * CalculationField - Optimized to prevent re-render loops with proper dependency tracking
 * @param {object} props - expects metadata and mode
 */
const CalculationField = React.memo(({ metadata, mode }) => {
  const {
    watch,
    setValue,
    register,
    formState: { errors }
  } = useFormContext();

  // Track initialization and previous values
  const [isInitialized, setIsInitialized] = React.useState(false);
  const previousDependentValues = useRef({});
  const initialValueSet = useRef(false);

  // Only watch the specific dependent fields to prevent unnecessary re-renders
  const watchedFields = useMemo(() => {
    if (!metadata.dependsOn || !Array.isArray(metadata.dependsOn)) {
      return [];
    }
    return [...metadata.dependsOn, metadata.id]; // Include own field to track current value
  }, [metadata.dependsOn, metadata.id]);

  // Watch only the specific fields we care about
  const watchedValues = watch(watchedFields);

  // Extract current value and dependent values
  const currentValue = watchedValues[watchedFields.length - 1]; // Last item is the current field value
  const dependentFieldValues = watchedValues.slice(0, -1); // All except the last

  // Helper function to extract numeric value from complex objects
  const extractNumericValue = useCallback((value, fieldName) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    // Handle simple numeric values
    if (typeof value === 'number') {
      return value;
    }

    // Handle string numbers
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? null : parsed;
    }

    // Handle objects (like textAndUnit fields)
    if (typeof value === 'object' && !Array.isArray(value)) {
      // First, try to find a 'quantity' property
      if (
        'quantity' in value &&
        value.quantity !== null &&
        value.quantity !== undefined &&
        value.quantity !== ''
      ) {
        const parsed = parseFloat(value.quantity);
        return isNaN(parsed) ? null : parsed;
      }

      // If no quantity, look for the first numeric value in the object
      for (const [key, val] of Object.entries(value)) {
        if (typeof val === 'number') {
          return val;
        }
        if (typeof val === 'string' && !isNaN(parseFloat(val))) {
          return parseFloat(val);
        }
      }
    }

    return null;
  }, []);

  // Process dependent values
  const processedDependentValues = useMemo(() => {
    if (!metadata.dependsOn || !Array.isArray(metadata.dependsOn)) {
      return {};
    }

    const processed = {};
    metadata.dependsOn.forEach((fieldName, index) => {
      const rawValue = dependentFieldValues[index];
      processed[fieldName] = extractNumericValue(rawValue, fieldName);
    });

    return processed;
  }, [metadata.dependsOn, dependentFieldValues, extractNumericValue]);

  // Check if dependencies are ready
  const dependenciesReady = useMemo(() => {
    if (!metadata.dependsOn || !Array.isArray(metadata.dependsOn)) {
      return false;
    }

    return metadata.dependsOn.every(fieldName => {
      const processedValue = processedDependentValues[fieldName];
      return processedValue !== null && processedValue !== undefined;
    });
  }, [metadata.dependsOn, processedDependentValues]);

  // Function to check if dependent values have actually changed
  const haveDependentValuesChanged = useCallback(() => {
    const current = processedDependentValues;
    const previous = previousDependentValues.current;

    // If no previous values, consider it changed
    if (Object.keys(previous).length === 0) {
      return true;
    }

    // Check if any value has changed
    for (const fieldName of metadata.dependsOn || []) {
      if (current[fieldName] !== previous[fieldName]) {
        // console.log(`[${metadata.id}] Dependency ${fieldName} changed:`, {
        //   from: previous[fieldName],
        //   to: current[fieldName]
        // });
        return true;
      }
    }

    return false;
  }, [processedDependentValues, metadata.dependsOn, metadata.id]);

  // Calculate the result
  const calculateValue = useCallback(() => {
    if (!metadata.calculation || !dependenciesReady) {
      return null;
    }

    try {
      // Create calculation expression by replacing field references with actual values
      let expression = metadata.calculation;

      // Replace formValues.fieldName with actual numeric values
      metadata.dependsOn.forEach(fieldName => {
        const value = processedDependentValues[fieldName] || 0;
        const regex = new RegExp(`formValues\\.${fieldName}`, 'g');
        expression = expression.replace(regex, value.toString());
      });

      //   console.log(`[${metadata.id}] Performing calculation:`, {
      //     original: metadata.calculation,
      //     processed: expression,
      //     dependencies: processedDependentValues
      //   });

      // Use Function constructor for safe evaluation
      const result = new Function(`return ${expression}`)();

      if (isNaN(result) || !isFinite(result)) {
        return null;
      }

      return Math.round(result * 100) / 100; // Round to 2 decimal places
    } catch (error) {
      console.error(`[${metadata.id}] Calculation error:`, error);
      return null;
    }
  }, [
    metadata.calculation,
    metadata.id,
    metadata.dependsOn,
    processedDependentValues,
    dependenciesReady
  ]);

  // Handle initialization - set default value once
  useEffect(() => {
    if (!isInitialized) {
      if (
        mode === 'edit' &&
        metadata.defaultValue !== undefined &&
        metadata.defaultValue !== null
      ) {
        // console.log(
        //   `[${metadata.id}] Setting initial default value:`,
        //   metadata.defaultValue
        // );
        setValue(metadata.id, metadata.defaultValue, {
          shouldValidate: false,
          shouldDirty: false
        });
        initialValueSet.current = true;
      }
      setIsInitialized(true);
    }
  }, [isInitialized, mode, metadata.defaultValue, metadata.id, setValue]);

  // Main effect - only runs when dependencies actually change
  useEffect(() => {
    if (!isInitialized || !dependenciesReady) {
      return;
    }

    // Check if dependent values have actually changed
    if (!haveDependentValuesChanged()) {
      return; // No change, no need to recalculate
    }

    // Update the previous values reference
    previousDependentValues.current = { ...processedDependentValues };

    // Calculate new value
    const newCalculatedValue = calculateValue();

    // Only update if the calculated value is different from current value
    if (newCalculatedValue !== null && newCalculatedValue !== currentValue) {
      //   console.log(`[${metadata.id}] Updating calculated value:`, {
      //     from: currentValue,
      //     to: newCalculatedValue,
      //     mode
      //   });

      setValue(metadata.id, newCalculatedValue, {
        shouldDirty: false,
        shouldValidate: true
      });
    }
  }, [
    isInitialized,
    dependenciesReady,
    haveDependentValuesChanged,
    calculateValue,
    currentValue,
    metadata.id,
    setValue,
    mode
  ]);

  return (
    <Col>
      <Form.Group as={Col} sm={12} md={12} className="mb-3">
        <FormLabel
          label={metadata.label}
          required={metadata?.validation?.required}
          htmlFor={metadata.id}
        />
        <Form.Control
          id={metadata.id}
          {...register(metadata.id)}
          type="number"
          placeholder={metadata.placeholder}
          isInvalid={!!errors[metadata.id]}
          disabled={!metadata.isEditable}
          readOnly={!metadata.isEditable}
        />
        <ErrorFeedback error={errors[metadata.id]} />
      </Form.Group>
    </Col>
  );
});

CalculationField.displayName = 'CalculationField';

export default CalculationField;
